"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.onTransitPathsMouseLeave = exports.onTransitPathsMouseEnter = exports.unhoverPath = void 0;
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
/** This file encapsulates map events that apply to the path* layer, in any section */
const maplibre_gl_1 = require("maplibre-gl");
const ServiceLocator_1 = __importDefault(require("chaire-lib-common/lib/utils/ServiceLocator"));
const Path_1 = __importDefault(require("transition-common/lib/services/path/Path"));
const MapCursorHelper_1 = require("../MapCursorHelper");
const hoverPath = (pathId, coordinates, pathName) => {
    const popup = new maplibre_gl_1.Popup({
        offset: 10,
        anchor: 'bottom'
    });
    popup.setLngLat(coordinates);
    popup.setHTML(`<p>${pathName}</p>`);
    ServiceLocator_1.default.eventManager.emit('map.addPopup', pathId, popup);
};
const unhoverPath = (pathId) => {
    ServiceLocator_1.default.eventManager.emit('map.removePopup', pathId);
};
exports.unhoverPath = unhoverPath;
const onTransitPathSelectedMouseEnter = (e) => {
    if (e.features && e.features[0]) {
        (0, MapCursorHelper_1.addHoverClass)();
    }
};
const onTransitPathSelectedMouseLeave = () => {
    (0, MapCursorHelper_1.removeHoverClass)();
};
const onTransitPathWaypointMouseEnter = (e) => {
    if (e.features && e.features[0]) {
        (0, MapCursorHelper_1.addHoverClass)();
    }
};
const onTransitPathWaypointMouseLeave = () => {
    (0, MapCursorHelper_1.removeHoverClass)();
};
const onTransitPathWaypointSelectedMouseEnter = (e) => {
    if (e.features && e.features[0]) {
        (0, MapCursorHelper_1.addHoverClass)();
    }
};
const onTransitPathWaypointSelectedMouseLeave = () => {
    (0, MapCursorHelper_1.removeHoverClass)();
};
const onTransitPathsMouseEnter = (e) => {
    var _a, _b, _c;
    if (e.target.getZoom() >= 12 && e.features && e.features[0]) {
        const pathGeojson = e.features[0];
        const hoveredPathId = (_a = pathGeojson.properties) === null || _a === void 0 ? void 0 : _a.id;
        // Skip if hovering over the currently selected path - let transitPathsSelected handle it
        const selectedPath = (_b = ServiceLocator_1.default.selectedObjectsManager) === null || _b === void 0 ? void 0 : _b.getSingleSelection('path');
        if (selectedPath && selectedPath.getId() === hoveredPathId) {
            return;
        }
        // Skip hover on other paths when the selected path has unsaved changes
        // This prevents accidental path switching when editing
        const selectedLine = (_c = ServiceLocator_1.default.selectedObjectsManager) === null || _c === void 0 ? void 0 : _c.getSingleSelection('line');
        if (selectedPath && (selectedPath.hasChanged() || (selectedLine && selectedLine.hasChanged()))) {
            return;
        }
        const map = e.target;
        (0, MapCursorHelper_1.addHoverClass)();
        const path = new Path_1.default(ServiceLocator_1.default.collectionManager.get('paths').getById(hoveredPathId).properties, false, ServiceLocator_1.default.collectionManager);
        const line = path.getLine();
        if (map._hoverPathIntegerId && map._hoverPathId && map._hoverPathSource) {
            (0, exports.unhoverPath)(map._hoverPathId);
            map.setFeatureState({ source: map._hoverPathSource, id: map._hoverPathIntegerId }, { size: 2, hover: false });
        }
        map.setFeatureState({ source: pathGeojson.source, id: pathGeojson.id }, { size: 3, hover: true });
        // See https://github.com/alex3165/react-mapbox-gl/issues/506
        map._hoverPathIntegerId = pathGeojson.id;
        map._hoverPathId = hoveredPathId;
        map._hoverPathSource = pathGeojson.source;
        hoverPath(path.getId(), e.lngLat.toArray(), `${line ? line.toString() : ''} • ${path.toString(false)} (${path.attributes.direction})`);
        e.originalEvent.stopPropagation();
    }
};
exports.onTransitPathsMouseEnter = onTransitPathsMouseEnter;
const onTransitPathsMouseLeave = (e) => {
    const map = e.target;
    // Only decrement hover count if we have tracking state
    // (means we actually added hover in mouseenter, vs skipping for selected path)
    if (map._hoverPathIntegerId && map._hoverPathId && map._hoverPathSource) {
        (0, MapCursorHelper_1.removeHoverClass)();
        (0, exports.unhoverPath)(map._hoverPathId);
        e.target.setFeatureState({ source: map._hoverPathSource, id: map._hoverPathIntegerId }, { size: 2, hover: false });
        map._hoverPathIntegerId = null;
        map._hoverPathId = null;
        map._hoverPathSource = null;
    }
};
exports.onTransitPathsMouseLeave = onTransitPathsMouseLeave;
const pathLayerEventDescriptors = [
    {
        type: 'layer',
        layerName: 'transitPathsSelected',
        eventName: 'mouseenter',
        handler: onTransitPathSelectedMouseEnter
    },
    {
        type: 'layer',
        layerName: 'transitPathsSelected',
        eventName: 'mouseleave',
        handler: onTransitPathSelectedMouseLeave
    },
    {
        type: 'layer',
        layerName: 'transitPathWaypoints',
        eventName: 'mouseenter',
        handler: onTransitPathWaypointMouseEnter
    },
    {
        type: 'layer',
        layerName: 'transitPathWaypoints',
        eventName: 'mouseleave',
        handler: onTransitPathWaypointMouseLeave
    },
    {
        type: 'layer',
        layerName: 'transitPathWaypointsSelected',
        eventName: 'mouseenter',
        handler: onTransitPathWaypointSelectedMouseEnter
    },
    {
        type: 'layer',
        layerName: 'transitPathWaypointsSelected',
        eventName: 'mouseleave',
        handler: onTransitPathWaypointSelectedMouseLeave
    },
    { type: 'layer', layerName: 'transitPaths', eventName: 'mouseenter', handler: exports.onTransitPathsMouseEnter },
    { type: 'layer', layerName: 'transitPaths', eventName: 'mouseleave', handler: exports.onTransitPathsMouseLeave }
];
exports.default = pathLayerEventDescriptors;
//# sourceMappingURL=PathLayerMapEvents.js.map